import {
  IconButton,
  type IconButtonProps,
  Tooltip,
  useBoolean,
} from "@chakra-ui/react";
import { t } from "@lingui/macro";
import {
  type Project,
  type Task,
  UpdateProjectScopesDocument,
  type UpdateProjectScopesMutation,
  type UpdateProjectScopesMutationVariables,
  UpdateTaskScopesDocument,
  type UpdateTaskScopesMutation,
  type UpdateTaskScopesMutationVariables,
  UserScopeEnum,
} from "@src/__generated__/graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { client } from "@src/services/apollo-client";
import type { IconNames } from "@src/utils/types/iconNames";
import { type FunctionComponent, useState } from "react";

export type ExternalAccessIconProps =
  | {
      type: "task";
      id: Task["id"];
      scopes: Task["scopes"];
      isTemplate: boolean;
    }
  | {
      type: "project";
      id: Project["id"];
      scopes: Project["scopes"];
      isTemplate: boolean;
    };

const DATA_RESPONSE_MAP = {
  task: "updateTask",
  project: "updateProject",
} as const;

const ACCESS_SCOPE_MAP = {
  task: [UserScopeEnum.Client, UserScopeEnum.Partner],
  project: [UserScopeEnum.Client],
} as const;

export const ExternalAccessIcon: FunctionComponent<ExternalAccessIconProps> = ({
  type,
  id,
  scopes,
  isTemplate,
}) => {
  const [accessState, setAccessState] = useState<"enabled" | "disabled">(
    scopes?.length ? "enabled" : "disabled",
  );
  const [isLoading, setIsLoading] = useBoolean(false);

  const updateEntity = async (id: string, scopes: UserScopeEnum[]) => {
    const mutation = {
      task: UpdateTaskScopesDocument,
      project: UpdateProjectScopesDocument,
    }[type];
    return client.mutate<
      UpdateTaskScopesMutation | UpdateProjectScopesMutation,
      UpdateTaskScopesMutationVariables | UpdateProjectScopesMutationVariables
    >({
      mutation,
      variables: {
        input: {
          id,
          scopes,
          is_template: isTemplate,
        },
      },
    });
  };

  const actions: Record<
    typeof accessState,
    {
      tooltip: string;
      icon: IconNames;
      iconColor: IconButtonProps["color"];
      iconHoverColor?: IconButtonProps["color"];
      onClick: () => void;
    }
  > = {
    enabled: {
      tooltip: {
        task: t`Task is accessible for clients with granted access. Toggle to set private.`,
        project: t`Project is accessible for clients with granted access. Toggle to set private.`,
      }[type],
      icon: "client-access-allowed",
      iconColor: "green.500",
      iconHoverColor: "green.600",
      onClick: async () => {
        setIsLoading.on();
        const res = await updateEntity(id, []);
        setIsLoading.off();

        if ((res.data as any)?.[DATA_RESPONSE_MAP[type]].id) {
          setAccessState("disabled");
        }
      },
    },
    disabled: {
      tooltip: {
        task: t`Task is only accessible to internal agency users. Toggle to enable access for clients.`,
        project: t`Project is only accessible to internal agency users. Toggle to enable access for clients.`,
      }[type],
      icon: "client-access-locked",
      iconColor: "red.500",
      iconHoverColor: "red.600",
      onClick: async () => {
        setIsLoading.on();
        const res = await updateEntity(id, [...ACCESS_SCOPE_MAP[type]]);
        setIsLoading.off();

        if ((res.data as any)?.[DATA_RESPONSE_MAP[type]].id) {
          setAccessState("enabled");
        }
      },
    },
  };

  const currentAction = actions[accessState];

  return (
    <Tooltip label={currentAction.tooltip} placement="top">
      <IconButton
        color={currentAction.iconColor}
        opacity={accessState === "enabled" ? 1 : 0}
        _hover={{ color: currentAction.iconHoverColor }}
        _groupHover={{ opacity: 1 }}
        transition="opacity 0.2s"
        aria-label={currentAction.tooltip}
        colorScheme="gray"
        icon={<Icon name={currentAction.icon} w="5" h="5" />}
        isLoading={isLoading}
        onClick={(e) => {
          e.stopPropagation();
          currentAction.onClick();
        }}
        size="sm"
        variant="plain"
      />
    </Tooltip>
  );
};
